import { useMemo, useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { startTrace, stopTrace, getTraceStatus } from "../api/trace";
import { getTaskSummary, listTaskNotifications, markTaskNotificationsRead } from "../api/tasks";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dkPackLogo from "../assets/dk-pack-logo.jpg";

const drawerWidth = 280;
const NAV_BG = "#0B1220"; // deep navy
const NAV_BORDER = "rgba(148, 163, 184, 0.16)";
const NAV_TEXT = "rgba(255, 255, 255, 0.88)";
const NAV_MUTED = "rgba(255, 255, 255, 0.64)";
const BRAND_DARK = "#14421F"; // DK Pack logo koyu yesili

function safeParseUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function logout() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  window.location.href = "/login";
}

function getRouteTitle(pathname) {
  const map = [
    ["/customers", "Müşteriler"],
    ["/cards", "Kartlar"],
    ["/suppliers", "Tedarikçiler"],
    ["/users", "Kullanicilar"],
    ["/employees", "Kullanicilar"],
    ["/materials", "Malzemeler"],
    ["/work-center-groups", "Makine Grupları"],
    ["/work-centers", "İş Merkezleri"],
    ["/knives", "Bıçaklar"],
    ["/molds", "Kalıplar"],
    ["/shifts", "Vardiyalar"],
    ["/production/shift-entry", "Vardiya Üretim Girişi"],
    ["/sales-orders", "Siparişler"],
    ["/work-orders", "İş Emirleri"],
    ["/inventory", "Stok / Envanter"],
    ["/paper-reservations", "Rezervasyon"],
    ["/admin/raw-materials/hammadde", "Hammadde"],
    ["/admin/raw-materials/paper", "Hammadde Giriş - Kağıt"],
    ["/admin/raw-materials/ink", "Hammadde Giriş - Boya"],
    ["/admin/raw-materials/lak", "Hammadde Giriş - Lak"],
    ["/admin/raw-materials/box", "Hammadde Giriş - Koli"],
    ["/admin/raw-materials/label", "Hammadde Giriş - Etiket"],
    ["/admin/raw-materials/shrink", "Hammadde Giriş - Shrink"],
    ["/support/variant-dimensions", "Varyant Anahtarları"],
    ["/support/variant-options", "Varyant Seçenekleri"],
    ["/support/excel-import", "Excel Aktar"],
    ["/reports/work-order-approvals", "İş Bitiş Onayı Arama"],
    ["/planning/work-center-queue", "İş Merkezi Planlama"],
    ["/fason-takip", "Fason Takip"],
    ["/reports/production-status", "Üretim Durum Raporu"],
    ["/reports/quality-control", "Kalite Kontrol Onayları"],
    ["/chatbot", "Üretim Asistanı"],
    ["/cost-report", "Fiili Maliyet Raporu"],
    ["/operator/assigned", "Atanan İş Emirleri"],
    ["/tasks", "Görevler"],
  ];

  const found = map.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + "/"));
  return found ? found[1] : "KOBİ MES";
}

function NavItem({ to, label, selected, onNavigate }) {
  return (
    <ListItemButton
      component={NavLink}
      to={to}
      onClick={onNavigate}
      selected={selected}
      sx={{
        mx: 1,
        my: 0.25,
        borderRadius: 2,
        color: NAV_TEXT,
        "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        "&.Mui-selected": {
          bgcolor: "rgba(79, 70, 229, 0.95)",
          color: "#fff",
          "&:hover": { bgcolor: "rgba(79, 70, 229, 1)" },
        },
      }}
    >
      <ListItemText primary={label} />
    </ListItemButton>
  );
}

export default function AppShell({ children }) {
  const user = useMemo(() => safeParseUser(), []);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tracing, setTracing] = useState(false);
  const [traceLoading, setTraceLoading] = useState(false);
  const [taskSummary, setTaskSummary] = useState({ unread_count: 0, overdue_count: 0 });
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    getTraceStatus()
      .then((d) => setTracing(d?.tracing ?? false))
      .catch(() => {});
  }, []);

  const loadTaskNotifications = useCallback(async () => {
    try {
      const [summary, items] = await Promise.all([getTaskSummary(), listTaskNotifications(8)]);
      setTaskSummary(summary || { unread_count: 0, overdue_count: 0 });
      setNotifications(Array.isArray(items) ? items : []);
    } catch {
      // Eski veritabaninda migration uygulanana kadar kabuk ekrani calismaya devam eder.
    }
  }, []);

  useEffect(() => {
    loadTaskNotifications();
    const timer = window.setInterval(loadTaskNotifications, 60000);
    return () => window.clearInterval(timer);
  }, [loadTaskNotifications]);

  const openNotifications = async (event) => {
    setNotificationAnchor(event.currentTarget);
    await loadTaskNotifications();
    try {
      await markTaskNotificationsRead();
      setTaskSummary((prev) => ({ ...prev, unread_count: 0 }));
    } catch {}
  };

  const handleToggleTrace = async () => {
    setTraceLoading(true);
    try {
      if (tracing) {
        const data = await stopTrace();
        setTracing(false);
        alert(`Trace kaydedildi: ${data.file}\n\nDosya: backend/scripts/trace_logs/${data.file}`);
      } else {
        await startTrace();
        setTracing(true);
        alert("Trace başlatıldı. İşlemi yapın, sonra tekrar tıklayarak Trace Durdur ve Kaydet.");
      }
    } catch (err) {
      alert(err?.response?.data?.error?.message || "Trace işlemi başarısız.");
    } finally {
      setTraceLoading(false);
    }
  };

  const title = useMemo(() => getRouteTitle(pathname), [pathname]);
  const role = user?.role;

  const closeMobile = () => setMobileOpen(false);
  const toggleMobile = () => setMobileOpen((v) => !v);

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.2, color: "#fff" }}>
          KOBİ MES
        </Typography>
        <Typography variant="body2" sx={{ color: NAV_MUTED }}>
          {role === "operator"
            ? "Operatör Paneli"
            : role === "quality"
              ? "Kalite Paneli"
              : role === "depocu"
                ? "Depocu Paneli"
                : "Yönetim Paneli"}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: NAV_BORDER }} />

      {role === "operator" ? (
        <List
          dense
          subheader={
            <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
              OPERATÖR
            </ListSubheader>
          }
        >
          <NavItem to="/tasks" label="Görevlerim" selected={pathname === "/tasks"} onNavigate={closeMobile} />
          <NavItem
            to="/operator/assigned"
            label="Atanan İş Emirleri"
            selected={pathname === "/operator/assigned"}
            onNavigate={closeMobile}
          />
        </List>
      ) : role === "quality" ? (
        <List
          dense
          subheader={
            <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
              KALITE
            </ListSubheader>
          }
        >
          <NavItem to="/tasks" label="Görevlerim" selected={pathname === "/tasks"} onNavigate={closeMobile} />
          <NavItem
            to="/reports/quality-control"
            label="Kalite Kontrol Onaylari"
            selected={pathname === "/reports/quality-control"}
            onNavigate={closeMobile}
          />
        </List>
      ) : role === "yonetici" ? (
        <List
          dense
          subheader={
            <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
              YÖNETİCİ
            </ListSubheader>
          }
        >
          <NavItem
            to="/sales-orders"
            label="Siparişler"
            selected={pathname === "/sales-orders"}
            onNavigate={closeMobile}
          />
          <NavItem to="/tasks" label="Görevler" selected={pathname === "/tasks"} onNavigate={closeMobile} />
          <NavItem
            to="/reports/production-status"
            label="Üretim Durum Raporu"
            selected={pathname === "/reports/production-status"}
            onNavigate={closeMobile}
          />
          <NavItem
            to="/chatbot"
            label="Üretim Asistanı"
            selected={pathname === "/chatbot"}
            onNavigate={closeMobile}
          />
          <NavItem
            to="/cards"
            label="Kartlar"
            selected={
              pathname === "/cards" ||
              ["/customers", "/suppliers", "/users", "/employees", "/materials", "/work-center-groups", "/work-centers", "/knives", "/molds"].some(
                (p) => pathname === p
              )
            }
            onNavigate={closeMobile}
          />
        </List>
      ) : role === "depocu" ? (
        <>
          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                STOK/MAL GİRİŞ
              </ListSubheader>
            }
          >
            <NavItem
              to="/admin/raw-materials/hammadde"
              label="Hammadde"
              selected={
                pathname === "/admin/raw-materials/hammadde" ||
                [
                  "/admin/raw-materials/paper",
                  "/admin/raw-materials/ink",
                  "/admin/raw-materials/lak",
                  "/admin/raw-materials/box",
                  "/admin/raw-materials/label",
                  "/admin/raw-materials/shrink",
                ].some((p) => pathname === p)
              }
              onNavigate={closeMobile}
            />
            <NavItem to="/inventory" label="Stok / Envanter" selected={pathname === "/inventory"} onNavigate={closeMobile} />
          </List>

          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                ASİSTAN
              </ListSubheader>
            }
          >
            {/* depocu Üretim Asistanı'nda sadece Stok Hareketleri'ni görür. */}
            <NavItem
              to="/chatbot"
              label="Üretim Asistanı"
              selected={pathname === "/chatbot"}
              onNavigate={closeMobile}
            />
          </List>

          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                DESTEK TABLOLARI
              </ListSubheader>
            }
          >
            <NavItem
              to="/support/variant-options"
              label="Varyant Seçenekleri"
              selected={pathname === "/support/variant-options"}
              onNavigate={closeMobile}
            />
            <NavItem
              to="/support/excel-import"
              label="Excel Aktar"
              selected={pathname === "/support/excel-import"}
              onNavigate={closeMobile}
            />
            {/* Kartlar > Tedarikçiler: depocu sadece bu kartı görür/tedarikçi ekleyebilir. */}
            <NavItem
              to="/cards"
              label="Kartlar"
              selected={pathname === "/cards" || pathname === "/suppliers"}
              onNavigate={closeMobile}
            />
          </List>
        </>
      ) : (
        <>
          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                ÜRETİM
              </ListSubheader>
            }
          >
            <NavItem
              to="/sales-orders"
              label="Siparişler"
              selected={pathname === "/sales-orders"}
              onNavigate={closeMobile}
            />
            <NavItem to="/work-orders" label="İş Emirleri" selected={pathname === "/work-orders"} onNavigate={closeMobile} />
            <NavItem
              to="/production/shift-entry"
              label="Vardiya Üretim Girişi"
              selected={pathname === "/production/shift-entry"}
              onNavigate={closeMobile}
            />
            <NavItem to="/fason-takip" label="Fason Takip" selected={pathname === "/fason-takip"} onNavigate={closeMobile} />
            {/* Planlama üretim müdürü rolüne özel; admin de görür. */}
            {role === "uretim_muduru" || role === "admin" ? (
              <NavItem
                to="/planning/work-center-queue"
                label="Planlama"
                selected={pathname === "/planning/work-center-queue"}
                onNavigate={closeMobile}
              />
            ) : null}
            <NavItem to="/tasks" label="Görevler" selected={pathname === "/tasks"} onNavigate={closeMobile} />
          </List>

          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                STOK/MAL GİRİŞ
              </ListSubheader>
            }
          >
            {/* Hammadde stoğuna giriş depocu rolüne özel; admin de görür/kullanabilir
                (uretim_muduru bu varsayılan dalı da kullandığı için admin'e özel tutulur). */}
            {role === "admin" ? (
              <NavItem
                to="/admin/raw-materials/hammadde"
                label="Hammadde"
                selected={
                  pathname === "/admin/raw-materials/hammadde" ||
                  [
                    "/admin/raw-materials/paper",
                    "/admin/raw-materials/ink",
                    "/admin/raw-materials/lak",
                    "/admin/raw-materials/box",
                    "/admin/raw-materials/label",
                    "/admin/raw-materials/shrink",
                  ].some((p) => pathname === p)
                }
                onNavigate={closeMobile}
              />
            ) : null}
            <NavItem to="/inventory" label="Stok / Envanter" selected={pathname === "/inventory"} onNavigate={closeMobile} />
          </List>

          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                RAPORLAR
              </ListSubheader>
            }
          >
            <NavItem
              to="/reports/production-status"
              label="Üretim Durum Raporu"
              selected={pathname === "/reports/production-status"}
              onNavigate={closeMobile}
            />
            <NavItem
              to="/reports/quality-control"
              label="Kalite Kontrol Onayları"
              selected={pathname === "/reports/quality-control"}
              onNavigate={closeMobile}
            />
          </List>

          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                ASİSTAN
              </ListSubheader>
            }
          >
            <NavItem
              to="/chatbot"
              label="Üretim Asistanı"
              selected={pathname === "/chatbot"}
              onNavigate={closeMobile}
            />
          </List>

          <List
            dense
            subheader={
              <ListSubheader component="div" sx={{ bgcolor: "transparent", mt: 1, color: NAV_MUTED }}>
                DESTEK TABLOLARI
              </ListSubheader>
            }
          >
            <NavItem
              to="/cards"
              label="Kartlar"
              selected={
                pathname === "/cards" ||
                ["/customers", "/suppliers", "/users", "/employees", "/materials", "/work-center-groups", "/work-centers", "/knives", "/molds"].some(
                  (p) => pathname === p
                )
              }
              onNavigate={closeMobile}
            />
            <NavItem
              to="/support/variant-dimensions"
              label="Varyant Anahtarları"
              selected={pathname === "/support/variant-dimensions"}
              onNavigate={closeMobile}
            />
            <NavItem
              to="/support/variant-options"
              label="Varyant Seçenekleri"
              selected={pathname === "/support/variant-options"}
              onNavigate={closeMobile}
            />
            <NavItem
              to="/support/excel-import"
              label="Excel Aktar"
              selected={pathname === "/support/excel-import"}
              onNavigate={closeMobile}
            />
          </List>
        </>
      )}

      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ borderColor: NAV_BORDER }} />

      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, color: NAV_MUTED }}>
          {user?.email || ""}
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={logout}
          sx={{
            borderRadius: 2,
            borderColor: "rgba(255,255,255,0.22)",
            color: NAV_TEXT,
            "&:hover": { borderColor: "rgba(255,255,255,0.35)", bgcolor: "rgba(255,255,255,0.06)" },
          }}
        >
          Çıkış
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          borderBottom: "1px solid rgba(20, 66, 31, 0.14)",
          background:
            "linear-gradient(90deg, rgba(230,243,219,0.94) 0%, rgba(242,250,236,0.94) 50%, rgba(228,242,216,0.94) 100%)",
          backdropFilter: "blur(10px)",
          color: BRAND_DARK,
          boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 2px 12px rgba(20, 66, 31, 0.08)",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {!isDesktop ? (
            <Button
              variant="outlined"
              color="inherit"
              onClick={toggleMobile}
              sx={{ minWidth: 44, px: 1.25 }}
              aria-label="Menü"
            >
              ≡
            </Button>
          ) : null}

          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              flexGrow: 1,
              color: BRAND_DARK,
              textShadow: "0 1px 0 rgba(255,255,255,0.8)",
            }}
          >
            {title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mr: 0.5 }}>
            <Box
              component="img"
              src={dkPackLogo}
              alt="DK Pack Ambalaj"
              sx={{
                height: 40,
                width: 40,
                borderRadius: "12px",
                objectFit: "cover",
                border: "1px solid rgba(20, 66, 31, 0.16)",
                boxShadow: "0 2px 8px rgba(20, 66, 31, 0.22), 0 1px 0 rgba(255,255,255,0.9) inset",
              }}
            />
            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                fontWeight: 900,
                fontSize: 15,
                lineHeight: 1.15,
                letterSpacing: "1.6px",
                color: BRAND_DARK,
                whiteSpace: "nowrap",
                textShadow:
                  "0 1px 0 rgba(255,255,255,0.9), 0 2px 5px rgba(20, 66, 31, 0.32), 0 4px 10px rgba(20, 66, 31, 0.16)",
              }}
            >
              DK PACK AMBALAJ
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              alignSelf: "center",
              height: 26,
              width: "1px",
              bgcolor: "rgba(20, 66, 31, 0.18)",
              mr: 0.5,
            }}
          />
          <Button
            color="inherit"
            onClick={() => { window.location.href = "/tasks"; }}
            sx={{ textTransform: "none", minWidth: 0, px: 1 }}
            title={taskSummary.overdue_count ? `${taskSummary.overdue_count} gecikmiş görev` : "Görevler"}
          >
            <Badge badgeContent={taskSummary.overdue_count || taskSummary.unread_count} color="error" max={99}>✓</Badge>
          </Button>
          <Button
            color="inherit"
            onClick={openNotifications}
            sx={{ textTransform: "none", minWidth: 0, px: 1 }}
            aria-label="Görev bildirimleri"
          >
            <Badge badgeContent={taskSummary.unread_count} color="primary" max={99}>🔔</Badge>
          </Button>
          {user?.email === "ccihanbeyoglu@gmail.com" ? (
            <Button
              variant={tracing ? "contained" : "outlined"}
              color={tracing ? "error" : "primary"}
              size="small"
              onClick={handleToggleTrace}
              disabled={traceLoading}
              sx={{ textTransform: "none" }}
            >
              {tracing ? "Trace Durdur" : "Trace Başlat"}
            </Button>
          ) : null}
        </Toolbar>
      </AppBar>
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={() => setNotificationAnchor(null)}
        PaperProps={{ sx: { width: 360, maxWidth: "calc(100vw - 32px)" } }}
      >
        {notifications.length ? notifications.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => { setNotificationAnchor(null); window.location.href = "/tasks"; }}
            sx={{ whiteSpace: "normal", py: 1 }}
          >
            <Box>
              <Typography variant="body2" fontWeight={item.read_at ? 400 : 800}>{item.message}</Typography>
              <Typography variant="caption" color="text.secondary">{String(item.created_at || "").slice(0, 16)}</Typography>
            </Box>
          </MenuItem>
        )) : <MenuItem disabled>Yeni görev bildirimi yok.</MenuItem>}
      </Menu>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="navigation">
        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={isDesktop ? true : mobileOpen}
          onClose={closeMobile}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              borderRight: `1px solid ${NAV_BORDER}`,
              bgcolor: NAV_BG,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}
